import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { stats, values, timeline, PORTRAIT } from "@/data/content";

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return <span ref={ref}>{display}</span>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function About() {
  return (
    <section id="sobre" data-testid="about-section" className="relative bg-white py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          chapter="01"
          kicker="Quem é Rosangela"
          title="Da Zona rural para cuidar de gente"
          description="Uma história de serviço público construída porta a porta, entre a educação e a saúde, no coração do Extremo Sul da Bahia."
        />

        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="relative"
          >
            <div className="absolute -left-4 -top-4 h-full w-full rounded-[2rem] bg-brand-orange/20" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl">
              <img
                src={PORTRAIT}
                alt="Rosangela da Saúde em retrato oficial de campanha, com camisa polo azul"
                className="h-auto w-full object-cover"
                data-testid="about-portrait"
              />
            </div>
            <div className="absolute -bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-brand-dark px-6 py-3 shadow-xl">
              <BadgeCheck className="h-5 w-5 text-brand-orange" />
              <span className="whitespace-nowrap text-sm font-bold text-white">Especialista em Saúde Comunitária</span>
            </div>
          </motion.div>

          <div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="space-y-5 text-base sm:text-lg leading-relaxed text-slate-600"
            >
              <p>
                Nascida em <strong className="text-slate-900">Porto Seguro</strong>, no Extremo Sul da Bahia, Rosangela
                cresceu em uma família simples de <strong className="text-slate-900">oito irmãos</strong>, com os pés na
                zona rural e o coração na vida comunitária. Foi ali que aprendeu, desde cedo, que cuidar de quem está ao
                lado é um jeito de viver.
              </p>
              <p>
                São <strong className="text-slate-900">21 anos de serviço público</strong> dedicados à educação e à
                saúde. Licenciada em Pedagogia, com especialização em Educação de Jovens e Adultos (EJA), ela acredita
                que nunca é tarde para aprender — e que a educação é o caminho mais seguro para transformar vidas.
              </p>
              <p>
                Como técnica agente comunitária de saúde e especialista em Saúde Comunitária, soma{" "}
                <strong className="text-slate-900">21 anos servindo Eunápolis e o Extremo Sul</strong>: ouvindo,
                acolhendo e resolvendo, sempre de perto. Sua missão é clara — melhorar a qualidade de vida das pessoas
                do campo e da zona rural, com saúde, inclusão e cuidado de verdade.
              </p>
            </motion.div>

            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {values.map((v, i) => (
                <motion.div
                  key={v.title}
                  custom={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  data-testid={`value-card-${v.title.toLowerCase()}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-all duration-200 hover:-translate-y-1 hover:border-brand-royal/40 hover:shadow-lg"
                >
                  <p className="font-display text-sm font-bold text-brand-dark">{v.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{v.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              data-testid={`stat-card-${i}`}
              className="rounded-3xl bg-gradient-to-br from-brand-dark to-brand-royal p-7 text-white shadow-xl transition-transform duration-200 hover:-translate-y-2"
            >
              <p className="font-display text-5xl font-extrabold tracking-tight text-brand-orange">
                <Counter value={s.value} />
                {s.suffix}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-white/85">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-24">
          <motion.h3
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-12 font-display text-2xl sm:text-3xl font-bold text-slate-900"
          >
            Uma trajetória de serviço
          </motion.h3>
          <div className="relative border-l-2 border-brand-light/50 pl-8 sm:pl-12">
            {timeline.map((t, i) => (
              <motion.div
                key={t.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                data-testid={`timeline-item-${i}`}
                className="relative pb-12 last:pb-0"
              >
                <span className="absolute -left-[41px] top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand-orange text-[10px] font-extrabold text-white ring-4 ring-white sm:-left-[57px]">
                  {i + 1}
                </span>
                <h4 className="font-display text-lg sm:text-xl font-bold text-brand-dark">{t.title}</h4>
                <p className="mt-2 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-600">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
